import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Plus as AddIcon, Trash2 as DeleteIcon, Link2 as LinkIcon } from 'lucide-react';
import { Button, Chip, Switch } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { mcpService, HealthStatus, type AgentMcpServerAssignmentDto, type McpServerDto } from '../../services/mcpService';
import { useNotificationStore } from '../../store/notificationStore';
import { useConfirmDialog } from '../common/ConfirmDialog';

interface AgentMcpServerListProps {
  agentId: string;
  readOnly?: boolean;
}

const healthColor = (status: string): 'green' | 'red' | 'amber' | 'default' => {
  switch (status) {
    case HealthStatus.Healthy:
      return 'green';
    case HealthStatus.Unhealthy:
      return 'red';
    case HealthStatus.Degraded:
      return 'amber';
    default:
      return 'default';
  }
};

/**
 * Lists MCP servers assigned to an agent with add, toggle enable, and remove.
 */
export const AgentMcpServerList = ({ agentId, readOnly = false }: AgentMcpServerListProps) => {
  const [assignments, setAssignments] = useState<AgentMcpServerAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [availableServers, setAvailableServers] = useState<McpServerDto[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { addNotification } = useNotificationStore();
  const confirm = useConfirmDialog();

  const loadAssignments = async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await mcpService.getAgentMcpServers(agentId);
      setAssignments(list);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load MCP servers';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, [agentId]);

  const loadAvailableServers = async () => {
    setLoadingAvailable(true);
    try {
      const result = await mcpService.getMcpServers({ pageSize: 100, isActive: true });
      const assignedIds = new Set(assignments.map((a) => a.mcpServerId));
      setAvailableServers(result.items.filter((s) => !assignedIds.has(s.id)));
    } catch {
      setAvailableServers([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const openAddDialog = () => {
    setAddDialogOpen(true);
    loadAvailableServers();
  };

  const handleAssign = async (mcpServerId: string) => {
    setAssigningId(mcpServerId);
    try {
      await mcpService.assignMcpToAgent(agentId, { mcpServerId, isEnabled: true });
      addNotification('MCP server assigned', 'success');
      loadAssignments();
      loadAvailableServers();
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to assign', 'error');
    } finally {
      setAssigningId(null);
    }
  };

  const handleToggleEnabled = async (assignment: AgentMcpServerAssignmentDto) => {
    setTogglingId(assignment.id);
    try {
      await mcpService.assignMcpToAgent(agentId, {
        mcpServerId: assignment.mcpServerId,
        isEnabled: !assignment.isEnabled,
      });
      addNotification(assignment.isEnabled ? 'MCP server disabled' : 'MCP server enabled', 'success');
      loadAssignments();
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to update', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemove = async (assignmentId: string) => {
    const ok = await confirm({
      title: 'Remove MCP server',
      message: 'Remove this MCP server from the agent?',
      confirmText: 'Remove',
    });
    if (!ok) return;
    setRemovingId(assignmentId);
    try {
      await mcpService.removeMcpFromAgent(agentId, assignmentId);
      addNotification('MCP server removed', 'success');
      loadAssignments();
      loadAvailableServers();
    } catch (err: any) {
      addNotification(err.response?.data?.message || err.message || 'Failed to remove', 'error');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <LoadingSkeleton className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h6 className="text-lg font-semibold">
          MCP Servers
        </h6>
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" onClick={openAddDialog}>
            <AddIcon size={16} className="mr-1" />
            Add MCP Server
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4">
          <InlineErrorPanel message={error} action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />
        </div>
      )}

      {assignments.length === 0 ? (
        <p className="text-sm text-[var(--mui-palette-text-secondary)]">
          No MCP servers assigned. Add an MCP server to give this agent access to its tools.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--mui-palette-divider)]">
              <th className="px-3 py-2 text-left">Server</th>
              <th className="px-3 py-2 text-left">Status</th>
              {!readOnly && <th className="px-3 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id} className="border-b border-[var(--mui-palette-divider)] last:border-b-0">
                <td className="px-3 py-2">
                  <span className="text-sm font-medium">
                    {a.mcpServerName}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {togglingId === a.id ? (
                      <LoadingSkeleton className="h-5 w-5" />
                    ) : (
                      <Switch
                        checked={a.isEnabled}
                        disabled={readOnly}
                        onCheckedChange={() => handleToggleEnabled(a)}
                      />
                    )}
                    <Chip
                      label={a.isEnabled ? 'Enabled' : 'Disabled'}
                      color={a.isEnabled ? 'green' : 'default'}
                    />
                  </div>
                </td>
                {!readOnly && (
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-500"
                      onClick={() => handleRemove(a.id)}
                      disabled={removingId === a.id}
                      aria-label="Remove MCP server"
                    >
                      {removingId === a.id ? <LoadingSkeleton className="h-5 w-5" /> : <DeleteIcon size={16} />}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add MCP Server</DialogTitle>
        <DialogContent>
          {loadingAvailable ? (
            <div className="flex justify-center py-8">
              <LoadingSkeleton className="h-7 w-7" />
            </div>
          ) : availableServers.length === 0 ? (
            <p className="text-sm text-[var(--mui-palette-text-secondary)]">
              No other MCP servers available to assign. Create an MCP server first.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--mui-palette-divider)]">
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Health</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {availableServers.map((s) => (
                  <tr key={s.id} className="border-b border-[var(--mui-palette-divider)] last:border-b-0">
                    <td className="px-3 py-2">
                      <span className="text-sm font-medium block">
                        {s.name}
                      </span>
                      <span className="text-xs text-[var(--mui-palette-text-secondary)] break-all">
                        {s.serverUrl}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Chip label={s.healthStatus} color={healthColor(s.healthStatus)} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        disabled={!!assigningId}
                        onClick={() => handleAssign(s.id)}
                      >
                        {assigningId === s.id ? <LoadingSkeleton className="h-4 w-4 mr-1" /> : <LinkIcon size={16} className="mr-1" />}
                        Assign
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="ghost" onClick={() => setAddDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
